import _ from "lodash";
import React from "react";

import classes from "./MemberList.scss";
import {
  getDefaultNameSortPreference,
  getOrganizationId,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

import { useI18n } from "Hooks";
import { isIdEqual, onEnterOrSpaceKeyDown, prepareLoadMoreFeed } from "Utils";
import {
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import withLoader from "UIComponents/GqlWithLoader";
import withDerivedProps from "lib/withDerivedProps";
import Loading from "UIComponents/Loading";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { getSearchedMembersInChatQuery } from "MessagingV2/modules/MessagingQuery";
import { useHandleScroll } from "Utils/CustomHooks";
import MemberCard from "../../MemberCard";
import { withChatOptions } from "Platform/routes/MessagingV2/providers/ChatOptionsProvider";
import { handleManageMemberV2RemovedOrBlocked } from "MessagingV2/modules/MessagingModule";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import { Avatar } from "@toddle-edu/ds-web";
import { TeacherAddOutlined } from "@toddle-edu/ds-icons";

const MemberCardSkeleton = () => (
  <div className={classes.memberCardSkeleton}>
    <SkeletonLoader variant="rounded" width={40} height={40} />
    <div className={classes.memberCardSkeletonContent}>
      <SkeletonLoader variant="rounded" width="80%" height={14} />
      <SkeletonLoader variant="rounded" width="40%" height={12} />
    </div>
  </div>
);

const LoadingComponent = () => (
  <div className={classes.smallLoadingContainer}>
    {_.times(8, index => (
      <MemberCardSkeleton key={index} />
    ))}
  </div>
);

const AddMemberButton = props => {
  const { showAddMemberButton, searchText, onAddMember } = props;
  const { t } = useI18n(["common"]);

  const onKeyPressHandle = e => {
    onEnterOrSpaceKeyDown(e, onAddMember);
  };

  return showAddMemberButton && _.isEmpty(_.trim(searchText)) ? (
    <div
      className={classes.addMemberCard}
      onClick={onAddMember}
      role="button"
      tabIndex={0}
      onKeyPress={onKeyPressHandle}
    >
      <Avatar
        icon={<TeacherAddOutlined />}
        dsVersion="2.0"
        shape="square"
        onClick={onAddMember}
        color="neutral"
        size="medium"
        theme="dark"
        iconType="icon"
        className={classes.addMemberAvatar}
      />
      <div className="text-label">{t("common:add_members")}</div>
    </div>
  ) : null;
};

const getIsMemberRemovable = ({
  user,
  canUpdateMember,
  loggedInUserId,
  isDepartmentChannel,
  canManageChannel,
  createdBy,
}) => {
  const userId = _.get(user, "id");
  const type = _.get(user, "type");
  if (isDepartmentChannel) {
    return (
      canManageChannel &&
      !isIdEqual(_.get(createdBy, "id"), userId) &&
      _.isEqual(type, PARENT)
    );
  }

  const isCurrentLoggedInUser = isIdEqual(loggedInUserId, userId);

  return canUpdateMember && !isCurrentLoggedInUser;
};

const MemberList = props => {
  const {
    onRemoveUser,
    canUpdateMember,
    defaultStudentNameSortPreference,
    loggedInUserType,
    loggedInUserId,
    searchText,
    users,
    membersParams,
    queryVariables,
    refetchUsers,
    chatId,
    showAddMemberButton,
    onAddMember,
    canManageChannel,
    createdBy,
    isDepartmentChannel,
    canBlockUser,
  } = props;

  const { t } = useI18n(["common"]);

  const containerRef = React.createRef();

  const {
    networkStatus,
    loadMoreUsers = _.noop,
    hasNextPage = false,
    isFetchingMore = false,
  } = membersParams || {};

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreUsers,
    hasNextPage,
    getScrollRef: () => containerRef?.current,
  });

  const isEmptyResult = _.isEmpty(users);

  if (isEmptyResult) {
    if (_.isEmpty(_.trim(searchText))) {
      return (
        <div className={classes.emptyScreenContainer}>
          <AddMemberButton
            showAddMemberButton={showAddMemberButton}
            searchText={searchText}
            onAddMember={onAddMember}
          />
          <div className={classes.emptyScreenContainerText}>
            {t("common:no_user_found")}
          </div>
        </div>
      );
    }

    return (
      <div className={classes.emptyScreenContainer}>
        <div className={classes.emptyScreenContainerText}>
          {t("common:no_user_found_for_label", {
            label: searchText,
          })}
        </div>
      </div>
    );
  }

  const handleRemoveUser = user => {
    // remove user from cache and refetch query with callback
    handleManageMemberV2RemovedOrBlocked({
      user,
      variables: queryVariables,
      isRemoveFlow: true,
    });
    onRemoveUser({
      user,
      callback: refetchUsers,
    });
  };

  return (
    <div className={classes.container} ref={containerRef}>
      <AddMemberButton
        showAddMemberButton={showAddMemberButton}
        searchText={searchText}
        onAddMember={onAddMember}
      />
      {_.map(users, user => {
        const { id, profileImage, alertTypes = [] } = user;
        const isBlocked = _.get(user, "isChatViewOnly", false);

        const isCurrentLoggedInUser = isIdEqual(loggedInUserId, id);

        let name = getFormattedNameForUserType({
          user,
          orderBy: defaultStudentNameSortPreference,
          isFormattedNameV2: true,
        });

        if (isCurrentLoggedInUser) {
          name = t("common:you_with_full_name", { fullName: name });
        }

        const onRemoveClick = () => handleRemoveUser(user);

        return (
          <MemberCard
            key={id}
            onRemoveClick={onRemoveClick}
            avatarId={id}
            mainText={name}
            isBlocked={isBlocked}
            subText={getSubText({
              loggedInUserRole: loggedInUserType,
              messagedUserObj: user,
              messagedUserRole: _.get(user, "type"),
              t,
              defaultStudentNameSortPreference,
            })}
            profileImage={profileImage}
            userType={_.get(user, "type")}
            canBlockUser={canBlockUser}
            isRemovable={getIsMemberRemovable({
              user,
              canUpdateMember,
              loggedInUserId,
              isDepartmentChannel,
              canManageChannel,
              createdBy,
            })}
            alertTypes={alertTypes}
          />
        );
      })}
      {isFetchingMore ? <Loading /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { chatId } = ownProps;
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const { user_type: loggedInUserType, id: loggedInUserId } = getUserInfo();

  return {
    defaultStudentNameSortPreference,
    loggedInUserType,
    loggedInUserId,
    isData: true,
    isLoading: false,
    chatId,
  };
};

const getUserTypeOrder = userType => {
  const userTypeOrder = [STUDENT, STAFF, PARENT];
  if (_.isEmpty(userType)) return [];
  return _.sortBy(userType, userType => {
    return _.indexOf(userTypeOrder, userType);
  });
};

const getDerivedProps = () => ({
  customLoadingComponent: <LoadingComponent />,
  showLoadingIcon: false,
});

const MemberListWithData = compose(
  withChatOptions(),
  connect(mapStateToProps),
  withDerivedProps(getDerivedProps),
  graphql(getSearchedMembersInChatQuery, {
    name: "getSearchedMembersInChat",
    options: props => {
      const { searchText, chatId, userType } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          chatId: chatId,
          input: {
            first: 20,
            searchText,
            userType: getUserTypeOrder(userType),
          },
        },
      };
    },
    props: ({ getSearchedMembersInChat }) => {
      const queryVariables = _.get(getSearchedMembersInChat, "variables", {});

      const membersNode = _.get(getSearchedMembersInChat, "node", {});

      const users = _.map(
        _.get(membersNode, "usersV2.edges", []),
        user => user?.node,
        []
      );

      const hasNextPage = _.get(
        membersNode,
        "usersV2.pageInfo.hasNextPage",
        false
      );

      const networkStatus = _.get(getSearchedMembersInChat, "networkStatus");

      const membersParams = {
        networkStatus,
        hasNextPage,
        isFetchingMore: _.isEqual(networkStatus, 3),
        loadMoreUsers: prepareLoadMoreFeed({
          variables: {
            ...queryVariables,
            input: {
              ...queryVariables.input,
              before: _.get(membersNode, "usersV2.pageInfo.endCursor"),
            },
          },
          queryData: getSearchedMembersInChat,
          key: "usersV2",
        }),
      };

      const refetchUsers = () => getSearchedMembersInChat.refetch();

      return {
        isData: !_.isEmpty(membersNode),
        isLoading: _.includes([1, 2], networkStatus),
        membersParams,
        users,
        queryVariables,
        refetchUsers,
      };
    },
  }),
  withLoader
)(MemberList);

export default MemberListWithData;
